<template>
    <div class="widget">
        <form class="entry-form" @submit.prevent="submitForm">
            <ul class="entry-form__tabs">
                <li class="entry-form__tab" v-for="(tab, index) in tabs" :key="index" :class="{ active: !tab.url }">
                    <span v-if="!tab.url">{{ tab.label }}</span>
                    <a v-else :href="tab.url">
                        <span>{{ tab.label }}</span>
                        <SvgIcon icon="external-link" height="1rem" width="1rem" />
                    </a>
                </li>
            </ul>
            <div class="entry-form__content">
                <h3>{{ singleJourney ? $t('entryForm.title.singular') : $t('entryForm.title.plural') }}</h3>
                <div class="entry-form__row">
                    <div class="entry-form__group">
                        <VSelect
                            :label="$t('entryForm.labels.route')"
                            :options="routeOptions"
                            :hasError="v$.route.$error"
                            v-model="v$.route.$model"
                        />
                    </div>
                </div>
                <div class="entry-form__row">
                    <VToggleSwitch :label="$t('entryForm.labels.singleJourney')" v-model="singleJourney" />
                </div>
                <div class="entry-form__row">
                    <div class="entry-form__group">
                        <VDatePicker 
                            v-model="v$.outwardDate.$model" 
                            :minDate="startOfToday()" 
                            :popover="{ visibility: 'focus', transition: 'none' }"
                        >
                            <template #default="{ inputValue, inputEvents }">
                                <VInput
                                    :label="$t('entryForm.labels.outwardDate')"
                                    :hasError="v$.outwardDate.$error"
                                    :modelValue="inputValue"
                                    autocomplete="off"
                                    v-on="inputEvents"
                                >
                                    <template #icon>
                                        <SvgIcon class="calendar-icon" icon="edit-calendar" height="1rem" />
                                    </template>
                                </VInput>
                            </template>
                        </VDatePicker>
                    </div>
                    <div class="entry-form__group" v-if="!singleJourney">
                        <VDatePicker 
                            v-model="v$.returnDate.$model" 
                            :minDate="state.outwardDate || startOfToday()" 
                            :popover="{ visibility: 'focus', transition: 'none' }"
                        >
                            <template #default="{ inputValue, inputEvents }">
                                <VInput
                                    :label="$t('entryForm.labels.returnDate')"
                                    autocomplete="off"
                                    :hasError="v$.returnDate.$error"
                                    :modelValue="inputValue"
                                    v-on="inputEvents"
                                >
                                    <template #icon>
                                        <SvgIcon class="calendar-icon" icon="edit-calendar" height="1rem" />
                                    </template>
                                </VInput>
                            </template>
                        </VDatePicker>
                    </div>
                </div>
                <VBtn type="submit">{{ $t("entryForm.buttons.submit") }}</VBtn>
            </div>
        </form>
        <InfoPanel :btnText="$t('entryForm.buttons.infoPanel')" />
    </div>
</template>

<script lang="ts" setup>
import { useI18n } from "vue-i18n";
import { ref, reactive, onMounted } from "vue";
import { EntryFormTab } from "@/models/front/EntryForm/EntryFormTab";
import { VBtn, VDatePicker, VInput, VSelect, VToggleSwitch } from "v-ferry-components";
import { outwardDateRequired, required, returnDateRequired } from "@/helpers/validators";
import useVuelidate from "@vuelidate/core";
import SvgIcon from "@/components/icons/SvgIcon.vue";
import InfoPanel from "./InfoPanel.vue";
import { EntryFormQueries } from "@/models/front/EntryForm/EntryFormState";
import { YYYYMMDD } from "@/helpers/dateHelpers";
import { startOfToday } from "date-fns";
import useRouteCodesQuery from "@/composables/settings/_useRouteCodesQuery";

const { t } = useI18n();

const singleJourney = ref(true);

const tabs = ref<EntryFormTab[]>([]);
const entryFormBookingUrl = ref<string>();

onMounted(() => {
    tabs.value = [
        { label: t(`entryForm.tabs.newCustomer`) }, 
        { label: t(`entryForm.tabs.existingCustomer`), url: document.getElementById("entryFormContainer")?.getAttribute("data-entryFormLoginUrl") ?? "/" },      
    ];
    entryFormBookingUrl.value = document.getElementById("entryFormContainer")?.getAttribute("data-entryFormBookingUrl") ?? "/";
});

const { data: routeOptions } = useRouteCodesQuery();

const state = reactive({
    route: "",
    outwardDate: {} as Date,
    returnDate: {} as Date,
});

const rules = {
    route: { required },
    outwardDate: { 
        required: () => outwardDateRequired(state.outwardDate)
    },
    returnDate: { 
        required: () => returnDateRequired(!singleJourney.value, state.returnDate, state.outwardDate)
    },
};

const v$ = useVuelidate(rules, state);

const submitForm = async () => {
    const isValid = await v$.value.$validate();

    if (!isValid) return;

    let queryParams: EntryFormQueries = {
        route: state.route,
        outwardDate: YYYYMMDD(state.outwardDate),
    };

    if (!singleJourney.value) {
        queryParams = {
            ...queryParams,
            returnDate: YYYYMMDD(state.returnDate),
        };
    }

    const searchParams = new URLSearchParams(queryParams).toString();
    const redirectUrl = `${entryFormBookingUrl.value}?${searchParams}`;

    window.location.href = redirectUrl;
};

</script>

<style lang="scss" scoped>
.widget {
    --entry-form__max-width: 600px;
    --entry-form__info-panel__max-width: 400px;

    display: inline-flex;
    flex-direction: column;
    position: relative;
    background-color: var(--c-base);
    border-radius: var(--border-radius);
    box-shadow: var(--box-shadow);

    @include media-breakpoint-up(xl) {
        flex-direction: row;
        max-width: var(--entry-form__max-width);
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
    }
}

.entry-form {
    display: flex;
    flex-wrap: wrap;
    flex: 1 0 auto;
    width: 100%;
    color: var(--c-primary);

    &__tabs {
        display: flex;
        flex: 100%;
        padding: 0;
        background-color: var(--c-background-light);
        border-top-left-radius: var(--border-radius);
        border-top-right-radius: var(--border-radius);
        overflow: hidden;

        @include media-breakpoint-up(xl) {
            flex-direction: row;
            border-top-right-radius: 0;
        }
    }

    &__tab {
        display: flex;
        justify-content: center;
        align-items: center;
        flex: 0 1 50%;
        padding: 0 15px;
        height: 60px;
        background-color: var(--c-background-light);

        &.active {
            background-color: var(--c-base);
        }

        a[href] {
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 10px;
            color: var(--c-primary);
            text-decoration: none;
        }

        span {
            display: block;
            font-weight: bold;
            white-space: nowrap;
            text-overflow: ellipsis;
        }
    }

    &__row {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
    }

    &__group {
        display: flex;
        flex-direction: column;
        gap: var(--input-label-spacing);
        flex: auto;

        &--horizontal {
            flex-direction: row;
        }

        > label {
            color: var(--c-primary);
            font-size: var(--v-select-font-size);
            font-weight: bold;
            line-height: 1;
        }
    }

    &__content {
        display: flex;
        flex-direction: column;
        gap: 10px;
        flex: 100%;
        padding: 20px;

        @include media-breakpoint-up(xl) {
            padding: 30px;
        }

        h3 {
            color: var(--c-primary);
            font-size: 1.125rem;
            line-height: 1;
        }
    }

    .v-btn {
        margin-top: 20px;
    }
}
</style>
